import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { emailValidator, filterBoolean, trackByValue } from '@core/common-legacy';
import { EmailProvidersActions, EmailProvidersSelectors } from '@core/email-providers-data';
import type { EmailProviderData } from '@core/email-providers-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { AppStore } from '@core/legacy-app-store-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogData,
  DialogHeaderComponent,
  DialogRef,
  FormErrorDirective,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  MessageComponent,
  RtStatefulComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

type State = {
  editing: boolean;
  loading: boolean;
  formErrors: string[];
  emailProvider: EmailProviderData;
  formValue: Partial<EmailProviderData> | null;
};

@Component({
  selector: 'rt-email-provider-mailbox',
  templateUrl: './email-provider-mailbox-dialog.component.html',
  styleUrls: ['./email-provider-mailbox-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    MessageComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    FormErrorDirective,
    CheckboxComponent,
    HeadingComponent,
    IconComponent,
    ButtonComponent,
    GroupComponent,
    ButtonLoadingDirective,
    RtTooltip,
    TranslatePipe,
  ],
})
export class EmailProviderMailboxDialogComponent extends RtStatefulComponent<State> {
  private readonly dialog = inject(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly appStore = inject(AppStore);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  protected readonly cdRef: ChangeDetectorRef;

  public trackByValue = trackByValue;
  public editing: boolean = false;
  public loading: boolean;
  public form: UntypedFormGroup;
  public errors: { required: string; email: string } = {
    required: this.i18n.translate('app.common.form.required_error'),
    email: this.i18n.translate('app.common.invalid_email'),
  };

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);
    const data = inject<{
      emailProvider: EmailProviderData;
    }>(DialogData);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      editing: false,
      loading: false,
      formErrors: [],
      emailProvider: data.emailProvider,
      formValue: null,
    });
  }

  public ngOnInit(): void {
    const emailProvider =
      this.state.emailProvider.provider === 'mailbox'
        ? this.state.emailProvider
        : <EmailProviderData>{};
    this.form = this.formBuilder.group({
      email: [emailProvider.email || '', [Validators.required, emailValidator]],
      username: [emailProvider.username || '', [Validators.required]],
      password: [emailProvider.password || '', [Validators.required]],
      smtpServer: [emailProvider.smtpServer || '', [Validators.required]],
      smtpPort: [emailProvider.smtpPort || '', [Validators.required]],
      smtpUseSsl: [!!emailProvider.smtpUseSsl],
      imapServer: [emailProvider.imapServer || '', [Validators.required]],
      imapPort: [emailProvider.imapPort || '', [Validators.required]],
      imapUseSsl: [!!emailProvider.imapUseSsl],
      provider: ['mailbox'],
    });
    this.subscribeToEvents();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancelClick(): void {
    this.dialog.close();
  }

  public submit(): void {
    const value = {
      ...this.form.value,
      imapUseSsl: !!this.form.value.imapUseSsl,
      smtpUseSsl: !!this.form.value.smtpUseSsl,
    };
    this.store.dispatch(EmailProvidersActions.update({ payload: value }));

    this.store
      .select(EmailProvidersSelectors.error)
      .pipe(filterBoolean, take(1), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(({ error }) => {
        let formErrors = [];
        if (error && error.error && error.error.error) {
          formErrors = error.error.error;
        }
        this.setState({ ...this.state, loading: false, formErrors });
      });
  }

  public edit(): void {
    this.setState({
      ...this.state,
      editing: true,
    });
  }

  public get mailboxConnected(): boolean {
    return this.state.emailProvider.connected && this.state.emailProvider.provider === 'mailbox';
  }

  private subscribeToEvents(): void {
    this.store
      .select(EmailProvidersSelectors.isPending)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(isPending => {
        this.setState({ ...this.state, loading: isPending, formErrors: [] });
      });

    this.actions$
      .pipe(
        ofType(EmailProvidersActions.updateSuccess),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.dialog.close();
      });

    this.form.valueChanges
      .pipe(debounceTime(150), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(formValue => {
        this.setState({ ...this.state, formValue: formValue });
      });
  }
}
